import type { ICreateFulfillmentGroupCmd } from "@/data/commands/catalog/CreateFulfillmentGroupCmd";
import { Factory } from "@/data/dao/Factory";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import type { IFulfillmentGroup } from "@/data/model/FulfillmentGroup";

import { translate } from "@/shared/localization/language";

import { MultiLanguageInput } from "@/portal/formControls/MultiLanguageInput";
import { NumberInput } from "@/portal/formControls/NumberInput";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";
import  { createVueComponent, type VueComponentInstance } from "@/shared/utils/vue";
import StandardServiceDurationOverwritePerShiftForm from "@/portal/views/StandardServiceDurationOverwritePerShiftForm.vue";
import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { exceptionTypes } from "@/data/util/Config";
import { i18n } from "@/portal/Trans";

export class FulfillmentGroupForm extends BaseEntityForm<IFulfillmentGroup, ICreateFulfillmentGroupCmd> {
    private readonly minUnfulfilledOrders = 5;

    private nameField: MultiLanguageInput;
    private $internalNameField: JQuery;
    private $internalNoteField: JQuery;
    private standardProductionDurationField: NumberInput;
    private standardServiceDurationField: NumberInput;
    private maxUnfulfilledOrdersField: NumberInput;
    private $orderField: JQuery;
    private $overwriteStandardServiceDurationPerShiftForm: JQuery;
    private overwriteStandardServiceDurationPerShiftForm: VueComponentInstance<typeof StandardServiceDurationOverwritePerShiftForm>

    constructor(element: JQuery) {
        super(element, Factory.getInstance<CatalogDao>(CatalogDao).fulfillmentGroupDataSource);

        this.nameField = MultiLanguageInput.fromInput(this.$form.find('.form-control[name="name"]'));
        this.nameField.onChange = (e) => {
            this.updateName(translate(e));
        };

        this.$internalNameField = this.$form.find('.form-control[name="internalname"]');
        this.$internalNoteField = this.$form.find('.form-control[name="internalnote"]');

        this.standardProductionDurationField = new NumberInput(this.$form.find('.form-control[name="standardProductionDuration"]'), {
            min: 1,
            max: 1439,
            step: 1,
            decimals: 0,
        });

        this.standardServiceDurationField = new NumberInput(this.$form.find('.form-control[name="standardServiceDuration"]'), {
            min: 1,
            max: 1439,
            step: 1,
            decimals: 0,
        });
        
        this.maxUnfulfilledOrdersField = new NumberInput(
            this.$form.find('.form-control[name="maxunfulfilledorders"]'),
            {
                min: this.minUnfulfilledOrders,
                max: 1000,
                step: 1,
                decimals: 0,
            }
        );

        this.$orderField = this.$form.find('.form-control[name="order"]')["TouchSpin"]({
            min: 1,
            max: 10000,
            step: 1,
            buttondown_class: "btn btn-secondary",
            buttonup_class: "btn btn-secondary",
            verticalbuttons: true,
            verticalup: '<i class="la la-plus"></i>',
            verticaldown: '<i class="la la-minus"></i>',
        });
        
       this.$overwriteStandardServiceDurationPerShiftForm = this.$form.find('.override-standard-service-duration-per-shift-container');
        this.overwriteStandardServiceDurationPerShiftForm = createVueComponent(
            StandardServiceDurationOverwritePerShiftForm,
            {},
            this.$overwriteStandardServiceDurationPerShiftForm[0]
        ).mountedComponent;
    }

    public setFormData(model?: IFulfillmentGroup) {
        this.updateName(model === undefined ? "" : translate(model.name));
        this.nameField.setValues(model === undefined ? undefined : model.name);
        this.$internalNameField.val(model === undefined || model.internalName === undefined ? "" : model.internalName);
        this.$internalNoteField.val(model === undefined || model.internalNote === undefined ? "" : model.internalNote);
        this.maxUnfulfilledOrdersField.setValue(model?.maxUnfulfilledOrders);
        this.standardProductionDurationField.setValue(model === undefined ? 1 : model.standardProductionDuration);
        this.standardServiceDurationField.setValue(model?.standardServiceDuration);
        this.$orderField.val(model === undefined ? undefined : (model.order as any));
        
        this.overwriteStandardServiceDurationPerShiftForm.setData(model?.standardServiceDurationOverwrites ?? [])
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getFormData($form: JQuery): ICreateFulfillmentGroupCmd {
        const getValidatedOverwrites = this.overwriteStandardServiceDurationPerShiftForm.getValidatedData();
        
        const data: ICreateFulfillmentGroupCmd = {
            name: this.nameField.getValues(),
            internalName: this.$internalNameField.val().length === 0 ? undefined : this.$internalNameField.val(),
            internalNote: this.$internalNoteField.val().length === 0 ? undefined : this.$internalNoteField.val(),
            standardProductionDuration: this.standardProductionDurationField.getValue()!,
            standardServiceDuration: this.standardServiceDurationField.getValue(),
            order: this.$orderField.val().length !== 0 ? parseInt(this.$orderField.val()) : undefined,
            maxUnfulfilledOrders: this.maxUnfulfilledOrdersField.getValue(),
            standardServiceDurationOverwrites: getValidatedOverwrites.overwrites ?? [],
        };

        let hasError = false;

        if (!getValidatedOverwrites.isValid) {
            hasError = true;
        }
        
        if (
            data.name.de === undefined &&
            data.name.en === undefined &&
            data.name.fr === undefined &&
            data.name.it === undefined
        ) {
            hasError = true;
            this.nameField.setError(true);
        } else {
            if (
                (data.name.de !== undefined && data.name.de.length > 80) ||
                (data.name.fr !== undefined && data.name.fr.length > 80) ||
                (data.name.it !== undefined && data.name.it.length > 80) ||
                (data.name.en !== undefined && data.name.en.length > 80)
            ) {
                hasError = true;
                this.nameField.setError(true);
            } else {
                this.nameField.setError(false);
            }
        }

        // validate standard production duration
        if (
            data.standardProductionDuration === undefined ||
            isNaN(data.standardProductionDuration) ||
            data.standardProductionDuration <= 0 ||
            data.standardProductionDuration > 1439
        ) {
            hasError = true;
            this.standardProductionDurationField.setError(true);
        } else {
            this.standardProductionDurationField.setError(false);
        }

        // validate standard service duration
        if (data.standardServiceDuration != null &&
            ((isNaN(data.standardServiceDuration) ||
                data.standardServiceDuration <= 0 ||
                data.standardServiceDuration > 1439))
        ) {
            hasError = true;
            this.standardServiceDurationField.setError(true);
        } else {
            this.standardServiceDurationField.setError(false);
        }


        if (data.maxUnfulfilledOrders != null && data.maxUnfulfilledOrders < this.minUnfulfilledOrders) {
            hasError = true;
            this.maxUnfulfilledOrdersField.setError(true);
        } else {
            this.maxUnfulfilledOrdersField.setError(false);
        }

        if (data.internalName !== undefined && data.internalName.length > 80) {
            hasError = true;
            this.$internalNameField.addClass("is-invalid");
        }

        if (data.order !== undefined && isNaN(data.order)) {
            hasError = true;
            this.$orderField.addClass("is-invalid");
        }

        return (hasError ? undefined : data) as any;
    }

    protected resolveErrorMessage(e, b: DaoError): string | undefined {
        if (b?.additionalData?.reason == null) {
            return undefined;
        }

        switch (b.additionalData.reason) {
            case exceptionTypes.missingStandardServiceDurationForSeatsException:
                return i18n("item.missingStandardServiceDurationForSeats");
            case exceptionTypes.fulfillmentGroupCannotBeRemovedException:
                return i18n("item.fulfillmentGroupCannotBeRemoved");
        }
        return b.additionalData.reason;
    }

}
